import {
  buildTree,
  deliverableComparator,
  epicComparator,
  flattenVisible,
  isFiltersActive,
  makeFilterContext,
  matchesFilters,
  orderSprints,
  sprintColorScale,
} from '@mona-roadmap/domain';
import type { Epic, FilterState, SortState, FlatRow, Metadata, ProductGoal, RoadmapSnapshot, StatusFieldKey, StatusOption, TreeNode, ViewMode } from '@mona-roadmap/domain';
import { useMemo } from 'react';
import { computeExpanded, type ExpansionOverrides } from '../lib/expansion';

export interface RoadmapModel {
  roots: TreeNode[];
  rows: FlatRow[];
  expanded: Set<string>;
  filtersActive: boolean;
  goalsById: Map<string, ProductGoal>;
  epicsById: Map<string, Epic>;
  sprintColors: Record<string, { bg: string; fg: string }>;
  optionColor: (field: StatusFieldKey, label: string | null) => string | undefined;
  /** Number of deliverables passing the filters. */
  matchedCount: number;
  totalCount: number;
}

/** Derives everything the grid renders from the cached snapshot + metadata + UI state. All memoised. */
export function useRoadmapModel(snapshot: RoadmapSnapshot, metadata: Metadata, filters: FilterState, view: ViewMode, overrides: ExpansionOverrides, sort: SortState): RoadmapModel {
  const { productGoals, epics, deliverables } = snapshot;
  const filtersActive = isFiltersActive(filters);

  const filterCtx = useMemo(() => makeFilterContext(epics, productGoals), [epics, productGoals]);
  const matches = useMemo(() => (filtersActive ? (d: (typeof deliverables)[number]) => matchesFilters(d, filters, filterCtx) : undefined), [filtersActive, filters, filterCtx]);

  const comparators = useMemo(() => {
    const goalNames = new Map(productGoals.map((g) => [g.id, g.name]));
    const byIndex = (o: StatusOption[]) => [...o].sort((a, b) => a.index - b.index).map((x) => x.label);
    const ctx = {
      goalName: (id: string) => goalNames.get(id),
      statusOrder: byIndex(metadata.options.status),
      scopingOrder: byIndex(metadata.options.scopingStatus),
      sprintOrder: orderSprints(metadata.options.sprint),
    };
    return { deliverable: deliverableComparator(sort, ctx), epic: epicComparator(sort) };
  }, [sort, productGoals, metadata.options.status, metadata.options.scopingStatus, metadata.options.sprint]);

  const roots = useMemo(
    () => buildTree({ productGoals, epics, deliverables, view, doneStatuses: metadata.doneStatuses, matches, filtersActive, sort: comparators }),
    [productGoals, epics, deliverables, view, metadata.doneStatuses, matches, filtersActive, comparators],
  );
  const expanded = useMemo(() => computeExpanded(roots, overrides, filtersActive), [roots, overrides, filtersActive]);
  const rows = useMemo(() => flattenVisible(roots, expanded), [roots, expanded]);

  const goalsById = useMemo(() => new Map(productGoals.map((g) => [g.id, g])), [productGoals]);
  const epicsById = useMemo(() => new Map(epics.map((e) => [e.id, e])), [epics]);

  const sprintColors = useMemo(() => sprintColorScale(orderSprints(metadata.options.sprint)), [metadata.options.sprint]);
  const colorMaps = useMemo(() => {
    const maps = new Map<StatusFieldKey, Map<string, StatusOption>>();
    for (const key of Object.keys(metadata.options) as StatusFieldKey[]) maps.set(key, new Map(metadata.options[key].map((o) => [o.label, o])));
    return maps;
  }, [metadata.options]);
  const optionColor = useMemo(
    () => (field: StatusFieldKey, label: string | null) => (label ? colorMaps.get(field)?.get(label)?.color : undefined),
    [colorMaps],
  );

  const matchedCount = useMemo(() => (matches ? deliverables.filter(matches).length : deliverables.length), [deliverables, matches]);

  return { roots, rows, expanded, filtersActive, goalsById, epicsById, sprintColors, optionColor, matchedCount, totalCount: deliverables.length };
}
